import HttpService from "../services/HttpService";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import BusinessIcon from "@mui/icons-material/Business";

import { Fragment, useState } from "react";
import RefreshingIndicator from "../components/RefreshingIndicator";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import MoveBalance from "../components/MoveBalance";
import AddCompanyBalance from "../components/AddCompanyBalance";
import Movements from "../components/Movements";
import FundingAccounts from "../components/FundingAccounts";

export default function ManageBalance() {
  const [moveBalanceDlgData, moveAddBalanceDlgData] = useState(false);
  const [addCompanyDlg, setAddCompanyDlg] = useState(false);
  const [viewMovementsDlgData, setViewMovementsDlgData] = useState(false);
  const [viewAllMovementsDlgData, setViewAllMovementsDlgData] = useState(false);
  const [fundingAccountsDlgData, setFundingAccountsDlgData] = useState(false);
  const [includeSubdepartments, setIncludeSubdepartments] = useState(false);

  const viewMovementsDlgFunct = (event) => {
    return (
      <Dialog
        open={viewMovementsDlgData}
        onClose={() => {}}
        maxWidth="xl"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MenuBookIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              View Movements
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setViewMovementsDlgData();
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <Movements
            title={"Movements from " + viewMovementsDlgData.companyName}
            idBalance={viewMovementsDlgData.idBalance}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const viewAllMovementsDlgFunct = (event) => {
    return (
      <Dialog
        open={viewAllMovementsDlgData}
        onClose={() => {}}
        maxWidth="xl"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MenuBookIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              View All Movements
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setViewAllMovementsDlgData();
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <Movements title={"Movements from all balances "} idBalance={-1} />
        </DialogContent>
      </Dialog>
    );
  };

  const onAddBalanceClick = (e, params) => {
    params.idBalance = params.id;
    params.action = "CREDIT";
    moveAddBalanceDlgData(params);
  };

  const onDeductBalanceClick = (e, params) => {
    params.idBalance = params.id;
    params.action = "DEBIT";
    moveAddBalanceDlgData(params);
  };

  const onViewMovementsClick = (e, params) => {
    params.idBalance = params.id;
    setViewMovementsDlgData(params);
  };

  const onAddCompanyClick = () => setAddCompanyDlg(true);

  const queryConst = {
    main: {
      name: "balances",
      url: `/balance`,
    },
    count: {
      name: "balancesCount",
      url: "/balance/count",
    },
  };

  const dataColumns = [
    { field: "id", headerName: "ID", width: 90, hidden: true },
    {
      field: "companyName",
      headerName: "Company",
      width: 300,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.companyName}
          </Typography>
        );
      },
    },
    {
      field: "parentCompanyName",
      headerName: "Parent Company",
      width: 250,
      renderCell: (params) => {
        if (!params.value) return "";
        return (
          <Typography
            sx={{ fontSize: 12, mb: 0, color: "text.secondary" }}
            gutterBottom
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "balance",
      headerAlign: "right",
      headerName: "Balance",
      width: 200,
      align: "right",
      renderCell: (params) => {
        if (params.value == null) return "";
        const valueFormatted =
          "$ " +
          Number(params.value).toLocaleString("en-US", {
            minimumFractionDigits: 2,
          });
        return <div style={{ fontSize: "13px" }}>{valueFormatted}</div>;
      },
    },
    {
      field: "detail",
      headerAlign: "right",
      width: 150,
      headerName: "Actions",
      align: "right",
      renderCell: (params) => (
        <div>
          <Tooltip title="Increase Balance">
            <Button style={{ minWidth: "10px", color: "green" }}>
              <AddIcon
                onClick={(e) => onAddBalanceClick(e, params.row)}
                variant="contained"
              />
            </Button>
          </Tooltip>
          <Tooltip title="Deduct Balance">
            <Button style={{ minWidth: "10px", color: "red" }}>
              <RemoveIcon
                onClick={(e) => onDeductBalanceClick(e, params.row)}
                variant="contained"
              />
            </Button>
          </Tooltip>
          <Tooltip title="View movements">
            <Button style={{ minWidth: "10px" }}>
              <MenuBookIcon
                onClick={(e) => onViewMovementsClick(e, params.row)}
                variant="contained"
              />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  async function fetchMainData() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `${queryConst.main.url}`
    );
    return response;
  }

  const { data, isFetching, isLoading, isError } = useQuery(
    [queryConst.main.name],
    () => fetchMainData()
  );

  const moveBalanceDlg = (event) => {
    return (
      <Dialog
        open={moveBalanceDlgData}
        onClose={() => {}}
        PaperProps={{
          style: {
            width: "900px", // You can adjust the width as needed
            maxWidth: "none", // This prevents the maxWidth restriction
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {moveBalanceDlgData.action === "CREDIT" ? (
              <AddIcon sx={{ color: "white" }} />
            ) : (
              <RemoveIcon sx={{ color: "white" }} />
            )}
            <Typography
              variant="h6"
              sx={{
                color: "white",
              }}
            >
              {moveBalanceDlgData.action === "CREDIT"
                ? "Increase Balance"
                : "Deduct Balance"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              moveAddBalanceDlgData();
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <MoveBalance
            companyName={moveBalanceDlgData.companyName}
            open={moveBalanceDlgData ? true : false}
            action={moveBalanceDlgData.action}
            idBalance={moveBalanceDlgData.id}
            handleClose={() => {
              moveAddBalanceDlgData();
            }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const addCompanyDlgFunct = (event) => {
    return (
      <Dialog open={addCompanyDlg} onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Add Company
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setAddCompanyDlg(false);
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <AddCompanyBalance handleClose={() => setAddCompanyDlg(false)} />
        </DialogContent>
      </Dialog>
    );
  };

  const fundingAccountsDlgFunct = () => {
    return (
      <Dialog
        open={fundingAccountsDlgData}
        onClose={() => setFundingAccountsDlgData(false)}
        maxWidth="xl"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Funding Accounts
            </Typography>
          </Box>
          <IconButton
            onClick={() => setFundingAccountsDlgData(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          {fundingAccountsDlgData ? <FundingAccounts /> : null}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box>
      <Fragment>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ marginRight: "20px" }}>
            <Grid container alignItems="center">
              <Grid item pr={2}>
                <h1>Balances</h1>
              </Grid>
              <Grid item sx={{ paddingLeft: "1rem" }}>
                <Button variant="outlined" onClick={onAddCompanyClick}>
                  Add Company
                </Button>
              </Grid>
              <Grid item sx={{ paddingLeft: "1rem" }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setViewAllMovementsDlgData(!viewAllMovementsDlgData);
                  }}
                >
                  View All Movements
                </Button>
              </Grid>
              <Grid item sx={{ paddingLeft: "1rem" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeSubdepartments}
                      onChange={(e) =>
                        setIncludeSubdepartments(e.target.checked)
                      }
                    />
                  }
                  label="Include Subdepartments"
                />
              </Grid>
            </Grid>
          </div>
          <Button
            variant="contained"
            onClick={() => setFundingAccountsDlgData(true)}
          >
            Funding Accounts
          </Button>
          {isFetching ? <RefreshingIndicator /> : ""}
        </div>
        {isLoading ? (
          <CircularProgress color="inherit" />
        ) : !isError ? (
          <div style={{ height: "calc(100vh - 250px)", width: "100%" }}>
            <DataGrid
              disableSelectionOnClick
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              rowHeight={40}
              pageSize={10}
              rowsPerPageOptions={[10]}
              columnVisibilityModel={{ id: false }}
              rows={
                data?.data?.filter((row) =>
                  includeSubdepartments
                    ? row.companyType === "COMPANY" ||
                      row.companyType === "SUB_DEPARTMENT"
                    : row.companyType === "COMPANY"
                ) ?? []
              }
              columns={dataColumns}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </div>
        ) : (
          <h4>An error ocurred, please try again later...</h4>
        )}
        {moveBalanceDlgData ? moveBalanceDlg() : ""}
        {addCompanyDlg ? addCompanyDlgFunct() : ""}
        {viewMovementsDlgData ? viewMovementsDlgFunct() : ""}
        {viewAllMovementsDlgData ? viewAllMovementsDlgFunct() : ""}
        {fundingAccountsDlgData ? fundingAccountsDlgFunct() : ""}
      </Fragment>
    </Box>
  );
}
